import { Alert, Card, Checkbox, LearnMoreLink, NavigationLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect } from 'react';
import {
  featureRestricted,
  features,
  getActiveBillingPlanKey,
} from '../../../../constants/features';
import { useEnv } from '../../../../context/env-context';
import { useHandlers } from '../../../../context/page-context';
import { useSettings } from '../../../../context/settings-context';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import { ResetSettingsButton } from './reset-settings-button';

export function QuotingEngineCard() {
  const form = useCustomerGroupBuilderForm();
  const settings = useSettings();
  const env = useEnv();

  const isQuotingEngineRestricted = featureRestricted(
    features.QUOTING,
    getActiveBillingPlanKey(env.billing.plan_key),
  );

  const { navigationHandler } = useHandlers();
  const isQuotingEngineEnabled = settings.quotingEnabled;
  const isLegacyQuoteEnabled = !isQuotingEngineEnabled || isQuotingEngineRestricted;

  // useEffect to watch if feature has been turned on, for fast response
  useEffect(() => {
    form.setFieldValue(
      'settings.quotingEngine.disabled',
      isLegacyQuoteEnabled || !isQuotingEngineEnabled || undefined,
    );
  }, [isLegacyQuoteEnabled, isQuotingEngineEnabled]);

  return (
    <Card showBorder>
      <Card.Header collapsible headingLevel="h2" icon="calculator">
        {SETTING_KEY_TO_LABEL.quotingEngine}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customer_group.components.quoting_engine_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/quoting" />
        </Text>
        {isLegacyQuoteEnabled ? (
          <Alert
            variant="info"
            title={t(
              `labels:${isQuotingEngineRestricted ? 'feature_restricted' : 'feature_not_enabled'}`,
            )}
            headingLevel="h3"
          >
            <div className="flex flex-col items-start gap-2">
              <span>
                {t(
                  `features:customer_group.components.quoting_engine_card.errors.${isQuotingEngineRestricted ? 'restricted' : 'not_enabled'}`,
                )}
              </span>
              <NavigationLink
                onClick={() =>
                  isQuotingEngineRestricted
                    ? navigationHandler.navigateById('plan', {
                        newTab: true,
                      })
                    : navigationHandler.navigate('/configuration/general', {
                        openInDashboard: true,
                        newTab: true,
                      })
                }
              >
                {t(isQuotingEngineRestricted ? 'links:upgrade_plan' : 'links:go_to_settings')}
              </NavigationLink>
            </div>
          </Alert>
        ) : (
          <form.Field name="settings.quotingEngine.customer_create_quote">
            {(field) => (
              <Checkbox
                key="quote"
                label={t(
                  'features:customer_group.components.quoting_engine_card.allow_submission.label',
                )}
                checked={field.state.value ?? false}
                description={t(
                  'features:customer_group.components.quoting_engine_card.allow_submission.description',
                )}
                onChange={() => field.handleChange((prev) => !prev)}
              />
            )}
          </form.Field>
        )}
        <div className="flex justify-start">
          <form.Field name="settings.quotingEngine">
            {(field) => <ResetSettingsButton field={field} />}
          </form.Field>
        </div>
      </Card.Body>
    </Card>
  );
}
